import { useMemo, useState } from "react"
import { router } from "@inertiajs/react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Avatar } from "@/components/ui/avatar"
import { Sparkline } from "@/components/ui/sparkline"
import { DualLineChart } from "@/components/ui/dual-line-chart"
import { InfoTooltip } from "@/components/ui/info-tooltip"
import { KpiCard } from "@/components/ui/kpi-card"

// /analytics/handlers
//
// Compares AI agents and human handlers over time. Backed by
// Portal::Analytics::Handlers::Presenter — see app/use_cases/portal/
// analytics/handlers/presenter.rb.

type Metric = "positive_close_rate" | "ttfr" | "ghosted_rate" | "rating_coverage" | "avg_close_time"

type ChartSeries = Record<Metric, { dates: string[]; ai: (number | null)[]; human: (number | null)[] }>

type ChartLatest = Record<Metric, {
  ai: number | null
  human: number | null
  delta: number | null
  aiVsPrior: number | null
  humanVsPrior: number | null
}>

type KpiTile = {
  value: number | null
  deltaVsPrior: number | null
  sparkline: (number | null)[]
}

type Standout = {
  kind: "ai" | "human"
  id: number
  name: string
  avatarUrl: string | null
  positiveCloseRate: number | null
  positiveCloseRateDelta: number | null
  avgTtfrSeconds: number | null
  ghostedRate: number | null
  ratingCoverage: number | null
} | null

type EntityRow = {
  kind: "ai" | "human"
  id: number
  name: string
  avatarUrl: string | null
  role: string | null
  peerGroupIds: number[]
  convosHandled: number
  positiveCloseRate: number | null
  positiveCloseRateDelta: number | null
  avgTtfrSeconds: number | null
  ghostedRate: number | null
  ratingCoverage: number | null
  closesPerDay: number
  rageHackLockouts: number
  sparkline30d: (number | null)[]
  peerBucket: string | null
}

type AgentPool = { id: number; name: string }

type SlaSeries = {
  dates: string[]
  firstResponseSlaAttainmentPct: (number | null)[]
  resolutionSlaAttainmentPct: (number | null)[]
}

type Props = {
  rangeKey: "today" | "7d" | "30d" | "custom"
  range: { from: string; to: string; days: number }
  chartSeries: ChartSeries
  chartLatest: ChartLatest
  kpis: {
    ghostedRate: KpiTile
    ratingCoverage: KpiTile
    avgTtfrSeconds: KpiTile
    positiveCloseRateDelta: KpiTile
  }
  standouts: {
    topHumanPerformer: Standout
    topAiPerformer: Standout
    mostImproved: Standout
    atRisk: Standout
    fastestButLowClose: Standout
  }
  entities: EntityRow[]
  agentPools: AgentPool[]
  slaSeries: SlaSeries
  idleResponseBreachTotal: number
}

const RANGES: Array<{ key: Props["rangeKey"]; label: string }> = [
  { key: "today", label: "Today" },
  { key: "7d",    label: "7d" },
  { key: "30d",   label: "30d" },
]

const METRIC_TABS: Array<{ key: Metric; label: string }> = [
  { key: "positive_close_rate", label: "Positive close rate" },
  { key: "ttfr",                label: "TTFR" },
  { key: "ghosted_rate",        label: "Ghosted rate" },
  { key: "rating_coverage",     label: "Rating coverage" },
  { key: "avg_close_time",      label: "Avg close time" },
]

export default function AnalyticsHandlers(props: Props) {
  const { rangeKey, chartSeries, kpis, standouts, entities, agentPools, slaSeries, idleResponseBreachTotal } = props

  const [activeMetric, setActiveMetric] = useState<Metric>("positive_close_rate")
  const [typeFilter, setTypeFilter] = useState<"all" | "ai" | "human">("all")
  const [poolFilter, setPoolFilter] = useState<number | "all">("all")
  const [search, setSearch] = useState("")
  const [sortKey, setSortKey] = useState<SortKey>("positiveCloseRate")
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc")

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir(sortDir === "desc" ? "asc" : "desc")
    } else {
      setSortKey(key)
      // Time-like columns default to ascending (faster = better); rate-like
      // default to descending (higher = better).
      setSortDir(key === "avgTtfrSeconds" || key === "ghostedRate" ? "asc" : "desc")
    }
  }

  const filteredEntities = useMemo(() => {
    const filtered = entities.filter((e) => {
      if (typeFilter !== "all" && e.kind !== typeFilter) return false
      if (search && !e.name.toLowerCase().includes(search.toLowerCase())) return false
      // Pool filter only applies to humans (AI agents have no peerGroupIds).
      // When a pool is picked, AI rows are hidden — pools are a humans concept.
      if (poolFilter !== "all") {
        if (e.kind !== "human") return false
        if (!e.peerGroupIds.includes(poolFilter)) return false
      }
      return true
    })
    const dir = sortDir === "desc" ? -1 : 1
    return filtered.slice().sort((a, b) => {
      const av = sortValue(a, sortKey)
      const bv = sortValue(b, sortKey)
      if (av == null && bv == null) return 0
      if (av == null) return 1   // nulls always last
      if (bv == null) return -1
      if (typeof av === "string" && typeof bv === "string") return av.localeCompare(bv) * dir
      return ((av as number) - (bv as number)) * dir
    })
  }, [entities, typeFilter, poolFilter, search, sortKey, sortDir])

  function setRange(key: Props["rangeKey"]) {
    router.visit(`/analytics/handlers?range=${key}`, { preserveState: false, preserveScroll: true })
  }

  return (
    <AppShell>
      <PageHeader
        eyebrow="Analytics"
        title="Performance"
        subtitle="Compare AI agents and human handlers over time."
        actions={
          <div className="flex flex-wrap gap-2">
            {RANGES.map((r) => (
              <Button key={r.key} variant={r.key === rangeKey ? "red" : "ghost"} size="sm" onClick={() => setRange(r.key)}>
                {r.label}
              </Button>
            ))}
          </div>
        }
      />

      {/* Chart + latest panel. Legend hides on phones — the metric tabs
          carry the meaning already and the chart line colors are
          consistent across the page. */}
      <Card className="p-4 sm:p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-semibold sm:text-lg">{METRIC_TABS.find((t) => t.key === activeMetric)?.label} over time</h2>
          <div className="hidden items-center gap-4 text-xs sm:flex">
            <LegendDot color="#ef4444" label="AI aggregate" />
            <LegendDot color="#facc15" label="Human aggregate" />
          </div>
        </div>
        <div className="mb-4 flex flex-wrap gap-1">
          {METRIC_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveMetric(t.key)}
              className={
                "rounded-control border px-3 py-1 text-xs " +
                (t.key === activeMetric
                  ? "border-brand-red/40 bg-brand-redSoft text-brand-red"
                  : "border-border bg-white/5 text-app-muted hover:text-app-text")
              }
            >
              {t.label}
            </button>
          ))}
        </div>
        <DualLineChart
          dates={chartSeries[activeMetric].dates}
          seriesA={chartSeries[activeMetric].ai}
          seriesB={chartSeries[activeMetric].human}
          labelA="AI aggregate"
          labelB="Human aggregate"
          formatY={metricFormatter(activeMetric)}
          formatTooltip={metricFormatter(activeMetric)}
          height={260}
        />
      </Card>

      {/* SLA attainment chart — sits directly under the positive close rate
          chart so operators can scan attainment alongside outcome quality.
          Per-day numerator = met, denominator = met + breached. Days with
          neither met nor breached return nil (chart renders a gap). */}
      <Card className="p-4 sm:p-5">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold sm:text-lg">SLA attainment over time</h2>
            <div className="text-xs text-app-muted">
              Met / (Met + Breached) per day. Untracked convos are excluded from the denominator.
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            {/* Legend dots hide on phone — the line colors are decoded
                inline in the tooltip and the idle-breaches chip carries
                the most actionable number. */}
            <span className="hidden items-center gap-3 sm:flex">
              <LegendDot color="#22c55e" label="First response" />
              <LegendDot color="#facc15" label="Resolution" />
            </span>
            <span className="rounded-control border border-border bg-white/5 px-3 py-1.5 text-app-muted">
              Idle breaches in range:{" "}
              <span className="font-semibold text-app-text">{idleResponseBreachTotal.toLocaleString()}</span>
            </span>
          </div>
        </div>
        <DualLineChart
          dates={slaSeries.dates}
          seriesA={slaSeries.firstResponseSlaAttainmentPct}
          seriesB={slaSeries.resolutionSlaAttainmentPct}
          labelA="First response"
          labelB="Resolution"
          colorA="#22c55e"
          colorB="#facc15"
          formatY={formatSlaPct}
          formatTooltip={formatSlaPct}
          height={260}
        />
      </Card>

      {/* KPI tiles */}
      <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 md:gap-4">
        <KpiCard label="Ghosted rate (WoW)"                          tile={kpis.ghostedRate}            formatter={formatPct}        fillColor="#ef4444" />
        <KpiCard label="Rating coverage (trend)"                     tile={kpis.ratingCoverage}         formatter={formatPct}        fillColor="#22c55e" />
        <KpiCard label="Avg TTFR (trend)"                            tile={kpis.avgTtfrSeconds}         formatter={formatSeconds}    fillColor="#22c55e" formatDelta={formatSecondsDelta} />
        <KpiCard label="Positive close rate delta (AI vs Human)"     tile={kpis.positiveCloseRateDelta} formatter={(v) => formatPp(v ?? 0)} fillColor="#facc15" />
      </section>

      {/* Standout cards */}
      <section>
        <div className="mb-3 flex items-center gap-1.5 text-sm text-app-muted">
          Standout this period
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <StandoutCard label="Top human performer"        tone="gold"    icon="🏆"
                        entity={standouts.topHumanPerformer}
                        primary={(e) => formatPct(e.positiveCloseRate)}
                        secondary={(e) => deltaPp(e.positiveCloseRateDelta, "vs peer avg")} />
          <StandoutCard label="Top AI performer"           tone="gold"    icon="👑"
                        entity={standouts.topAiPerformer}
                        primary={(e) => formatPct(e.positiveCloseRate)}
                        secondary={(e) => deltaPp(e.positiveCloseRateDelta, "vs AI peer avg")} />
          <StandoutCard label="Most improved"              tone="success" icon="↗"
                        entity={standouts.mostImproved}
                        primary={(e) => deltaPpRaw(e.positiveCloseRateDelta)}
                        secondary={(e) => deltaPp(e.positiveCloseRateDelta, "vs prior 30d")} />
          <StandoutCard label="At risk / needs review"     tone="error"   icon="⚠"
                        entity={standouts.atRisk}
                        primarySub="Ghosted rate"
                        primary={(e) => deltaPpRaw(e.positiveCloseRateDelta)}
                        secondary={() => ""} />
          <StandoutCard label="Fastest but low close-rate" tone="warning" icon="⚡"
                        entity={standouts.fastestButLowClose}
                        primary={(e) => `${formatSeconds(e.avgTtfrSeconds)} TTFR`}
                        secondary={(e) => `${formatPct(e.positiveCloseRate)} close rate`} />
        </div>
      </section>

      {/* Entity table. Filter bar wraps so search drops below the
          chips on a phone; table swaps for a mobile card list at <lg
          since the desktop column count (11) is unworkable on 360px. */}
      <Card className="overflow-hidden p-0">
        <header className="flex flex-wrap items-center gap-3 border-b border-border p-4">
          <div className="w-full text-sm font-semibold sm:w-auto">Review all handlers and AI</div>
          <div className="flex flex-wrap gap-1">
            {(["all", "ai", "human"] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setTypeFilter(k)}
                className={
                  "min-h-9 rounded-control border px-3 text-xs " +
                  (k === typeFilter
                    ? "border-brand-red/40 bg-brand-redSoft text-brand-red"
                    : "border-border bg-white/5 text-app-muted hover:text-app-text")
                }
              >
                {k === "all" ? `All (${entities.length})` : k === "ai" ? `AI (${entities.filter((e) => e.kind === "ai").length})` : `Human (${entities.filter((e) => e.kind === "human").length})`}
              </button>
            ))}
          </div>
          <select
            value={poolFilter === "all" ? "all" : String(poolFilter)}
            onChange={(e) => setPoolFilter(e.target.value === "all" ? "all" : Number(e.target.value))}
            className="min-h-9 rounded-control border border-border bg-white/5 px-3 text-sm text-app-text focus:border-brand-red/40 focus:outline-none"
          >
            <option value="all">All pools</option>
            {agentPools.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or entity…"
            className="min-h-9 w-full rounded-control border border-border bg-white/5 px-3 text-sm text-app-text placeholder:text-app-subtle focus:border-brand-red/40 focus:outline-none sm:ml-auto sm:w-72"
          />
        </header>
        {filteredEntities.length === 0 ? (
          <div className="p-6 text-sm text-app-muted">No handlers match.</div>
        ) : (
          <>
            {/* Mobile card list — < lg only. Shows the same metrics in
                a stacked layout so phones don't horizontal-scroll an
                11-column table. */}
            <ul className="divide-y divide-border lg:hidden">
              {filteredEntities.map((e, idx) => (
                <li key={`${e.kind}-${e.id}`} className="p-4">
                  <div className="mb-3 flex items-center gap-3">
                    <Avatar name={e.name} url={e.avatarUrl} size={36} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-app-text">{e.name}</div>
                      <div className="mt-1 flex items-center gap-2">
                        <Badge tone={e.kind === "ai" ? "red" : "gold"}>{e.kind === "ai" ? "AI" : "HUMAN"}</Badge>
                        <span className="text-xs text-app-subtle">#{idx + 1}</span>
                        {e.peerBucket && <span className="text-xs text-app-subtle">· {e.peerBucket}</span>}
                      </div>
                    </div>
                    <div style={{ width: 70 }} className="shrink-0">
                      <Sparkline
                        values={e.sparkline30d}
                        height={28}
                        variant="area"
                        fillColor={trendColorFor(e.positiveCloseRateDelta)}
                      />
                    </div>
                  </div>
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs sm:grid-cols-4">
                    <div>
                      <dt className="text-app-subtle">Positive</dt>
                      <dd className="text-app-text">{formatPct(e.positiveCloseRate)}</dd>
                      <dd className="text-[11px] text-app-subtle">{deltaPp(e.positiveCloseRateDelta, "")}</dd>
                    </div>
                    <div>
                      <dt className="text-app-subtle">TTFR</dt>
                      <dd className="text-app-text">{formatSeconds(e.avgTtfrSeconds)}</dd>
                    </div>
                    <div>
                      <dt className="text-app-subtle">Ghosted</dt>
                      <dd className="text-app-text">{formatPct(e.ghostedRate)}</dd>
                    </div>
                    <div>
                      <dt className="text-app-subtle">Coverage</dt>
                      <dd className="text-app-text">{formatPct(e.ratingCoverage)}</dd>
                    </div>
                    <div>
                      <dt className="text-app-subtle">Closes/day</dt>
                      <dd className="text-app-text">{e.closesPerDay.toFixed(1)}</dd>
                    </div>
                    <div>
                      <dt className="text-app-subtle">Lockouts</dt>
                      <dd className={e.kind === "ai" && e.rageHackLockouts > 0 ? "text-status-error" : "text-app-muted"}>
                        {e.kind === "ai" ? e.rageHackLockouts : "—"}
                      </dd>
                    </div>
                  </dl>
                </li>
              ))}
            </ul>
          </>
        )}
        {filteredEntities.length === 0 ? null : (
          <div className="hidden overflow-x-auto lg:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-xs uppercase tracking-wide text-app-subtle">
                <th className="px-3 py-2 text-right">Rank</th>
                <SortableTh label="Entity"               sortKey="name"               currentKey={sortKey} dir={sortDir} onToggle={toggleSort} align="left"
                  tip="Display name. Humans show first/last name; AI shows the agent name." />
                <SortableTh label="Type"                 sortKey="kind"               currentKey={sortKey} dir={sortDir} onToggle={toggleSort} align="left"
                  tip="HUMAN = a CompanyUser handled the convo after escalation. AI = the convo closed without ever being escalated." />
                <SortableTh label="Positive close rate"  sortKey="positiveCloseRate"  currentKey={sortKey} dir={sortDir} onToggle={toggleSort} align="right"
                  tip="Share of closed convos rated 👍. Numerator = thumbs-up; denominator = all closes (rated + unrated). Higher is better." />
                <SortableTh label="Avg TTFR"             sortKey="avgTtfrSeconds"     currentKey={sortKey} dir={sortDir} onToggle={toggleSort} align="right"
                  tip="Time to first response. AI: created_at → first agent message. Human: escalated_at → first reply from the assignee. Lower is better." />
                <SortableTh label="Ghosted rate"         sortKey="ghostedRate"        currentKey={sortKey} dir={sortDir} onToggle={toggleSort} align="right"
                  tip="Share of closes that resolved by idle sweep or rating-prompt timeout (state = auto_resolved or assumed_resolved). High values = handler stopped responding before the user did." />
                <SortableTh label="Rating coverage"      sortKey="ratingCoverage"     currentKey={sortKey} dir={sortDir} onToggle={toggleSort} align="right"
                  tip="(positive + negative) / total closes. The share of closes the user actually rated 👍/👎. Low coverage means the close-rate signal is unreliable." />
                <SortableTh label="Closes / day"         sortKey="closesPerDay"       currentKey={sortKey} dir={sortDir} onToggle={toggleSort} align="right"
                  tip="convos handled / days in the selected window. Volume signal — high alone is not a quality signal." />
                <th className="px-3 py-2 text-right">
                  <ColLabel label="Trend" tip="Per-day positive close rate over the window. Line color is tied to the WoW delta — green = improving, red = declining, gold = flat." />
                </th>
                <SortableTh label="Rage / hack lockouts" sortKey="rageHackLockouts"   currentKey={sortKey} dir={sortDir} onToggle={toggleSort} align="right"
                  tip="Count of convos that flipped to rage_locked or attempted_hack. Only meaningful for AI agents — humans show '—'." />
                <th className="px-3 py-2 text-right">
                  <ColLabel label="Vs peer avg" tip="Percentile bucket within the same kind (AI vs Human), ranked by positive close rate. Top 5% / 10% / 20% / Bottom 20%." />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredEntities.map((e, idx) => (
                <tr key={`${e.kind}-${e.id}`} className="text-app-text">
                  <td className="px-3 py-3 text-right text-app-muted">{idx + 1}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <Avatar name={e.name} url={e.avatarUrl} size={40} />
                      <div className="font-medium">{e.name}</div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <Badge tone={e.kind === "ai" ? "red" : "gold"}>{e.kind === "ai" ? "AI" : "HUMAN"}</Badge>
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div>{formatPct(e.positiveCloseRate)}</div>
                    <div className="text-xs text-app-subtle">{deltaPp(e.positiveCloseRateDelta, "")}</div>
                  </td>
                  <td className="px-3 py-3 text-right">{formatSeconds(e.avgTtfrSeconds)}</td>
                  <td className="px-3 py-3 text-right">{formatPct(e.ghostedRate)}</td>
                  <td className="px-3 py-3 text-right">{formatPct(e.ratingCoverage)}</td>
                  <td className="px-3 py-3 text-right">{e.closesPerDay.toFixed(1)}</td>
                  <td className="px-3 py-3 text-right">
                    <div style={{ width: 90 }} className="ml-auto">
                      <Sparkline
                        values={e.sparkline30d}
                        height={28}
                        variant="area"
                        fillColor={trendColorFor(e.positiveCloseRateDelta)}
                      />
                    </div>
                  </td>
                  <td className={"px-3 py-3 text-right " + (e.kind === "ai" && e.rageHackLockouts > 0 ? "text-status-error" : "text-app-muted")}>
                    {e.kind === "ai" ? e.rageHackLockouts : "—"}
                  </td>
                  <td className="px-3 py-3 text-right text-app-muted">{e.peerBucket ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>
    </AppShell>
  )
}

// ─── Subcomponents ───────────────────────────────────────────────────

// ─── Sortable header ─────────────────────────────────────────────────

type SortKey = "name" | "kind" | "positiveCloseRate" | "avgTtfrSeconds" | "ghostedRate" | "ratingCoverage" | "closesPerDay" | "rageHackLockouts"

function sortValue(e: EntityRow, key: SortKey): number | string | null {
  switch (key) {
    case "name":              return e.name.toLowerCase()
    case "kind":              return e.kind
    case "positiveCloseRate": return e.positiveCloseRate
    case "avgTtfrSeconds":    return e.avgTtfrSeconds
    case "ghostedRate":       return e.ghostedRate
    case "ratingCoverage":    return e.ratingCoverage
    case "closesPerDay":      return e.closesPerDay
    case "rageHackLockouts":  return e.kind === "ai" ? e.rageHackLockouts : null
  }
}

function SortableTh({ label, sortKey, currentKey, dir, onToggle, align, tip }: {
  label: string
  sortKey: SortKey
  currentKey: SortKey
  dir: "asc" | "desc"
  onToggle: (k: SortKey) => void
  align: "left" | "right"
  tip: string
}) {
  const active = sortKey === currentKey
  const arrow = !active ? "" : (dir === "desc" ? "↓" : "↑")
  return (
    <th className={`px-3 py-2 ${align === "right" ? "text-right" : "text-left"}`}>
      <div className={`inline-flex items-center gap-1 ${align === "right" ? "justify-end" : ""}`}>
        <button
          type="button"
          onClick={() => onToggle(sortKey)}
          className={`group inline-flex items-center gap-1 hover:text-app-text ${active ? "text-app-text" : ""}`}
        >
          {label}
          <span className="text-[10px] opacity-70">{arrow}</span>
        </button>
        <InfoTooltip>{tip}</InfoTooltip>
      </div>
    </th>
  )
}

function ColLabel({ label, tip }: { label: string; tip: string }) {
  return (
    <span className="inline-flex items-center justify-end gap-1">
      {label}
      <InfoTooltip>{tip}</InfoTooltip>
    </span>
  )
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5 text-app-muted">
      <span className="inline-block h-2 w-4 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  )
}

// Standout cards take a tone that drives the border color, the soft
// background tint, the icon color, and the highlighted-metric color.
// Tone-tinted backgrounds make the dashboard scannable — green for good,
// red for bad — without forcing the user to read every label.
const STANDOUT_TONE: Record<"gold" | "success" | "warning" | "error", { border: string; bg: string; icon: string; metric: string }> = {
  gold:    { border: "border-vip-gold/40",        bg: "bg-vip-gold/[0.04]",       icon: "text-vip-goldSoft",     metric: "text-vip-goldSoft" },
  success: { border: "border-status-success/40",  bg: "bg-status-success/[0.06]", icon: "text-status-success",   metric: "text-status-success" },
  warning: { border: "border-status-warning/40",  bg: "bg-status-warning/[0.05]", icon: "text-status-warning",   metric: "text-status-warning" },
  error:   { border: "border-status-error/40",    bg: "bg-status-error/[0.06]",   icon: "text-status-error",     metric: "text-status-error" },
}

function StandoutCard({ label, tone, icon, entity, primary, secondary, primarySub }: {
  label: string
  tone: "gold" | "success" | "warning" | "error"
  icon: string
  entity: Standout
  primary: (e: NonNullable<Standout>) => string
  secondary: (e: NonNullable<Standout>) => string
  primarySub?: string
}) {
  const t = STANDOUT_TONE[tone]
  return (
    <div className={`rounded-card border ${t.border} ${t.bg} p-4`}>
      <div className={`mb-3 flex items-center gap-1.5 text-sm ${t.icon}`}>
        <span aria-hidden>{icon}</span>
        <span className="font-medium">{label}</span>
      </div>
      {entity ? (
        <div className="flex items-center gap-3">
          <Avatar name={entity.name} url={entity.avatarUrl} size={68} />
          <div className="min-w-0">
            <div className="truncate font-medium text-app-text">{entity.name}</div>
            {primarySub && <div className="text-xs text-app-muted">{primarySub}</div>}
            <div className={`text-xl font-semibold tracking-tight ${t.metric}`}>{primary(entity)}</div>
            {secondary(entity) && <div className="mt-0.5 text-xs text-app-subtle">{secondary(entity)}</div>}
          </div>
        </div>
      ) : (
        <div className="text-sm text-app-subtle">No data yet.</div>
      )}
    </div>
  )
}

// ─── Formatters ──────────────────────────────────────────────────────

function metricFormatter(metric: Metric): (n: number | null) => string {
  switch (metric) {
    case "positive_close_rate":
    case "ghosted_rate":
    case "rating_coverage":
      return formatPct
    case "ttfr":
    case "avg_close_time":
      return formatSeconds
  }
}

// Trend column color: green if the entity is improving WoW, red if
// declining, gold/neutral if flat. Threshold of 1pp avoids flipping on
// noise.
function trendColorFor(delta: number | null): string {
  if (delta == null) return "#facc15"
  if (delta > 0.01) return "#22c55e"
  if (delta < -0.01) return "#ef4444"
  return "#facc15"
}

function formatPct(n: number | null): string {
  if (n == null) return "—"
  return `${(n * 100).toFixed(1)}%`
}

// SLA series ships pre-multiplied 0-100 percentages (e.g. 92.5), so we
// format them directly rather than treating them as 0-1 ratios.
function formatSlaPct(n: number | null): string {
  if (n == null) return "—"
  return `${n.toFixed(1)}%`
}

function formatSeconds(n: number | null): string {
  if (n == null) return "—"
  if (n < 60) return `${Math.round(n)}s`
  if (n < 3600) return `${Math.round(n / 60)}m`
  return `${(n / 3600).toFixed(1)}h`
}

function formatSecondsDelta(n: number): string {
  const s = n >= 0 ? "↑" : "↓"
  return `${s} ${formatSeconds(Math.abs(n))}`
}

function formatPp(n: number): string {
  const s = n >= 0 ? "↑" : "↓"
  return `${s} ${(Math.abs(n) * 100).toFixed(1)}pp`
}

function deltaPp(n: number | null, suffix: string): string {
  if (n == null) return suffix ? `— ${suffix}` : "—"
  return `${formatPp(n)}${suffix ? ` ${suffix}` : ""}`
}

// Like formatPp but uses signed +/- instead of arrows — used in the
// big-metric position of standout cards (e.g. "+9.4pp", "+1.2pp WoW").
function deltaPpRaw(n: number | null): string {
  if (n == null) return "—"
  const sign = n >= 0 ? "+" : "-"
  return `${sign}${(Math.abs(n) * 100).toFixed(1)}pp`
}
